import { useEffect, useState } from 'react'

import { bildLesen, type OutboxEntry } from '../lib/outbox'

/**
 * Adressen für die Bilder aus dem Korb — und wieder freigeben.
 *
 * **Seit 1.46.0 steht das hier und nicht mehr in `Wartend.tsx`**, weil die
 * wartende Sprechblase nicht mehr die einzige Stelle ist: die Karte zeigt
 * einen wartenden Punkt jetzt ebenfalls als Kärtchen, und der braucht
 * dasselbe Bild. Abgeschrieben wäre es die zweite Kopie derselben Regel --
 * und wie die auseinanderläuft, steht drei Absätze weiter unten schwarz auf
 * weiß.
 *
 * Ohne das Freigeben hielte jede wartende Nachricht ihre Fotos im Speicher,
 * solange die App läuft. Auf einem Telefon mit fünf wartenden Beiträgen ist
 * das kein Schönheitsfehler mehr.
 *
 * **Das Bild wird vorher gelesen** (1.38.0), und das ist der Kern.
 *
 * Aus dem Betrieb: „die Bilder sind da und nach einigen Sekunden wieder weg",
 * dann Fragezeichen; Chat wechseln und zurück, und das Spiel beginnt von
 * vorn. Was hier ankommt, ist ein Blob **aus der IndexedDB**, und der ist
 * nicht dasselbe wie einer im Speicher: WebKit lässt die dahinterliegende
 * Datenquelle nach kurzer Zeit los, und die Adresse zeigt danach ins Leere.
 * Ein Neuaufbau liest frisch und zeigt das Bild wieder — für ein paar
 * Sekunden.
 *
 * `bildLesen()` holt die Bytes heraus; der neue Blob gehört danach dem
 * Speicher und keiner Datenbank mehr. **Es ist dieselbe Funktion, die der
 * Ausgangskorb vor dem Upload nimmt**, und das ist kein Zufall: es ist
 * dieselbe Ursache. Ein Foto, das sich nicht mehr lesen lässt, brach den
 * Upload ohne Antwort ab — und das heißt in dieser Anwendung „kein Netz".
 *
 * **Bis 1.42.0 stand hier ein eigener, kürzerer Weg**, und der hat genau das
 * gekostet, was er sollte: der Korb bekam mit 1.41.0 einen zweiten Anlauf über
 * eine frisch geöffnete Datenbank, die Anzeige nicht. Aus dem Betrieb, mit
 * Bildschirmfotos: an der wartenden Blase standen erst drei Fotos, dann keins
 * mehr, und nach einem Neustart wieder drei. Zwei Stellen mit derselben Regel
 * laufen auseinander — deshalb gibt es sie jetzt nur einmal.
 *
 * **Und die Abhängigkeit hängt am Eintrag, nicht an der Liste.** Jeder
 * vergebliche Sendeversuch schreibt den Eintrag neu, und die Liste kommt dann
 * mit neuen Blob-Objekten zurück — an deren Identität geknüpft würde hier bei
 * jedem Anlauf alles weggeworfen und neu gemacht. Die Bytes eines wartenden
 * Fotos ändern sich nie.
 */
export function useWartendeBilder(eintrag: OutboxEntry, hoechstens?: number): string[] {
  const key = eintrag.key
  const bilder = eintrag.bilder
  const [urls, setUrls] = useState<string[]>([])
  /*
   * **Wie viele wirklich gelesen werden** (1.46.0).
   *
   * Die Sprechblase zeigt alle, das Kärtchen auf der Karte nur das erste --
   * und jedes Lesen holt die Bytes eines Fotos in den Speicher. Ohne diese
   * Grenze entpackte ein wartender Punkt mit fünf Fotos alle fünf, um eines zu
   * zeigen.
   */
  const anzahl = hoechstens === undefined ? bilder.length : Math.min(bilder.length, hoechstens)

  useEffect(() => {
    let lebt = true
    let angelegt: string[] = []

    void (async () => {
      const gelesen: string[] = []
      for (let i = 0; i < anzahl; i += 1) {
        /*
         * **The thumbnail where the basket has one** (1.78.0). A waiting
         * bubble shows 56-point tiles, and decoding the full photos for them
         * is memory iOS collects first -- with five waiting posts that is not
         * cosmetic. Read into memory like the photo (reason above); if it
         * fails, the photo itself as before.
         */
        const klein = eintrag.vorschauen?.[i] ?? null
        if (klein !== null) {
          try {
            gelesen.push(URL.createObjectURL(new Blob([await klein.arrayBuffer()], { type: klein.type || 'image/jpeg' })))
            continue
          } catch {
            // The photo below.
          }
        }
        try {
          gelesen.push(URL.createObjectURL(await bildLesen(eintrag, i)))
        } catch {
          // Ein Foto, das sich auch im zweiten Anlauf nicht lesen lässt,
          // bekommt keine Adresse. Der Korb sagt es an der Blase, sobald er es
          // zu senden versucht.
        }
      }
      if (!lebt) {
        gelesen.forEach((url) => URL.revokeObjectURL(url))
        return
      }
      angelegt = gelesen
      setUrls(gelesen)
    })()

    return () => {
      lebt = false
      angelegt.forEach((url) => URL.revokeObjectURL(url))
    }
    // Absichtlich am Eintrag und nicht an `bilder`: Grund im Doc-Block oben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, anzahl])

  return urls
}
